import LockIcon from "@mui/icons-material/Lock";
import CategoryIcon from "@mui/icons-material/Category";

type PriceCode = "free" | "lite" | "pro" | "premium" | "parked";

export type PlanLimit = {
  name: "products" | "catalogs" | "storage" | "users";
  limit: number;
  unit?: string;
  marks?: number[];
  unitPrice?: number;
  unitYearPrice?: number;
};

export type BillingPlan = {
  id: PriceCode;
  pos: number;
  name: string;
  price: number;
  description: string;
  features: { name: string; hint?: string; icon? }[];
  limits: PlanLimit[];
  aiCredits?: string;
  pro?: boolean;
};

export type PriceValue = {
  value: number;
  amt: string;
  cent: string;
  final: boolean;
};

export type PriceLayer = {
  original: PriceValue;
  discounted?: PriceValue;
  annual?: PriceValue;
  annualMonthly?: PriceValue;
  annualDiscounted?: PriceValue;
};

export type PriceModel = {
  prices: Map<PriceCode, PriceLayer>;
};

const annualPlanDiscount = 40;

/** The yearly-billing discount, as a percentage. Exported so upgrade CTAs can
 *  quote it without re-hardcoding "40%". */
export const ANNUAL_DISCOUNT_PERCENT = annualPlanDiscount;

/**
 * Monthly-equivalent price when billed yearly, for the plan's *base* price.
 * Presentation only — the amount actually charged is derived from the plan
 * code server-side, and per-plan slider add-ons are handled by
 * `buildPriceLayer`. Use this for headline CTA copy ("$29.40/mo billed
 * yearly"), not for anything that feeds a charge.
 */
export const annualMonthlyPrice = (monthlyPrice: number): number =>
  monthlyPrice * ((100 - annualPlanDiscount) / 100);

/** "49" / "29.40" — keeps round prices clean while showing real cents. */
export const formatPrice = (value: number): string => {
  const s = value.toFixed(2);
  return s.endsWith(".00") ? s.slice(0, -3) : s;
};

export const freePlan: BillingPlan = {
  id: "free",
  pos: 1,
  name: "Free",
  price: 0,
  description: "15 day trial",
  features: [
    { name: "All pre-built templates" },
    { name: "<b>10</b> pages / catalog" },
  ],
  limits: [
    {
      name: "products",
      limit: 20,
    },
    {
      name: "catalogs",
      limit: 2,
    },
    {
      name: "storage",
      limit: 10,
      unit: "MB",
    },
    {
      name: "users",
      limit: 1,
    },
  ],
};

export const parkedPlan: BillingPlan = {
  ...freePlan,
  id: "parked",
  name: "Parked",
  price: 5,
  description: "Pause and save",
};

export const billingPlans: BillingPlan[] = [
  {
    id: "lite",
    pos: 3,
    name: "Lite",
    price: 19,
    description: "Essential catalog creation",
    features: [
      { name: "<b>50</b> pages / catalog" },
    ],
    aiCredits: "$20",
    limits: [
      {
        name: "products",
        limit: 100,
      },
      {
        name: "catalogs",
        limit: 5,
      },
      {
        name: "storage",
        limit: 100,
        unit: "MB",
      },
      {
        name: "users",
        limit: 1,
      },
    ],
  },
  {
    id: "pro",
    pos: 8,
    name: "Pro",
    price: 49,
    pro: true,
    description: "Advanced features, higher limits, full control",
    features: [
      { name: "<b>Unlimited</b> pages / catalog" },
      {
        name: "Password protected catalogs",
        hint: "Password protect your catalogs or Showroom to control access",
        icon: LockIcon,
      },
      {
        name: "Multiple product showrooms",
        hint: "Automatic searchable and printable product catalogs",
        icon: CategoryIcon,
      },
    ],
    aiCredits: "$40",
    limits: [
      {
        name: "products",
        limit: 500,
        marks: [500, 1000, 2000, 4000, 6000],
        unitPrice: 0.02,
        unitYearPrice: 0.14 / ((12 * (100 - annualPlanDiscount)) / 100),
      },
      {
        name: "catalogs",
        limit: 10,
        marks: [10, 20, 30],
        unitPrice: 1,
      },
      {
        name: "storage",
        limit: 1,
        unit: "GB",
        marks: [1, 2, 5],
        unitPrice: 5,
      },
      {
        name: "users",
        limit: 2,
        marks: [2, 4, 6],
        unitPrice: 5,
      },
    ],
  },
  {
    id: "premium",
    pos: 14,
    name: "Premium",
    price: 199,
    description: "Ultimate enterprise solution",
    features: [
      { name: "<b>Everything in PRO</b>" },
      { name: "+ Priority Support" },
    ],
    aiCredits: "$100",
    limits: [
      {
        name: "products",
        limit: 10000,
      },
      {
        name: "catalogs",
        limit: 50,
      },
      {
        name: "storage",
        limit: 10,
        unit: "GB",
      },
      {
        name: "users",
        limit: 10,
      },
    ],
  },
];

/**
 * `cent` is always TWO digits. It used to be `((price % 1) * 100).toFixed(0)`,
 * which drops the leading zero: Pro at yearly + a 25% coupon is
 * `49 × 0.6 × 0.75 = 22.05` and rendered as **"$22.5"** on the plans page, the
 * one screen where a wrong price costs a sale. Splitting `toFixed(2)` also
 * carries correctly (22.999 → "23" + "00") where rounding the fraction alone
 * would have produced "22" + "100".
 */
const makePrice = (price: number, final: boolean): PriceValue => {
  const [amt, cent] = price.toFixed(2).split(".");
  return { value: price, amt, cent, final };
};

/** "$19" / "$22.05" — drops ".00" so round prices stay clean. */
export const priceText = (price: PriceValue): string =>
  price.cent === "00" ? `$${price.amt}` : `$${price.amt}.${price.cent}`;

export const buildPriceLayer = (
  plan: BillingPlan,
  annual: boolean,
  couponPercent?: number
): PriceLayer => {
  const discount =
    couponPercent > 0 && couponPercent < 100 ? (100 - couponPercent) / 100 : 1;

  const annualDiscount = (100 - annualPlanDiscount) / 100;

  let price = plan.price;
  let annualMonthlyPrice = price * annualDiscount;

  if (plan.pro) {
    plan.limits.forEach((limit) => {
      const q = limit.limit - limit.marks[0];
      price += q * limit.unitPrice;
      annualMonthlyPrice +=
        q * (limit.unitYearPrice || limit.unitPrice) * annualDiscount;
    });
  }

  const l: PriceLayer = {
    original: makePrice(price, discount === 1 && !annual),
    discounted:
      discount != 1 ? makePrice(price * discount, !annual) : undefined,
    annual: makePrice(annualMonthlyPrice * 12, discount === 1),
    annualDiscounted:
      discount != 1
        ? makePrice(annualMonthlyPrice * 12 * discount, true)
        : undefined,
    annualMonthly: makePrice(annualMonthlyPrice * discount, true),
  };

  return l;
};
